import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import {
  Brain, Upload, RefreshCw, ChevronLeft, Lightbulb,
  Target, Shield, Waves, Eye, AlertTriangle, CheckCircle,
  Dumbbell, Sparkles, FileText, Loader2, TrendingUp
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AIInsightsPage() {
  const { childId } = useParams();
  const navigate = useNavigate();
  const [child, setChild] = useState(null);
  const [intakeProfile, setIntakeProfile] = useState(null);
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [childRes, intakeRes] = await Promise.all([
        api.get(`/children/${childId}`),
        api.get(`/children/${childId}/intake-profile`),
      ]);
      setChild(childRes.data);
      setIntakeProfile(intakeRes.data?.intake_profile || null);
      if (childRes.data?.ai_insights) {
        setInsights(childRes.data.ai_insights);
      }
    } catch { toast.error('Failed to load data'); }
    setLoading(false);
  }, [childId]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleUploadPDF = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('Please upload a PDF file');
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await api.post(`/children/${childId}/intake-pdf`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setIntakeProfile(res.data.profile);
      toast.success('Intake form uploaded and processed!');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to upload PDF');
    }
    setUploading(false);
    e.target.value = '';
  };

  const generateInsights = async () => {
    setGenerating(true);
    try {
      const res = await api.get(`/children/${childId}/ai-insights`);
      setInsights(res.data);
      toast.success('AI insights generated!');
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail || '';
      if (status === 429) {
        toast.error(detail || 'AI budget exceeded. Add balance in Profile > Universal Key.');
      } else if (status === 504) {
        toast.error('AI is taking too long. Please try again in a moment.');
      } else {
        toast.error(detail || 'Failed to generate insights');
      }
    }
    setGenerating(false);
  };

  if (loading) return <div className="flex items-center justify-center p-12"><Loader2 className="w-8 h-8 animate-spin text-cyan-500" /></div>;

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Brain },
    { id: 'profile', label: 'Intake Profile', icon: FileText },
    { id: 'planning', label: 'Session Planning', icon: Target },
    { id: 'patterns', label: 'Patterns', icon: Eye },
  ];

  return (
    <div className="max-w-5xl mx-auto animate-fadeIn" data-testid="ai-insights-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6 gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="icon" className="rounded-full" onClick={() => navigate('/dashboard')} data-testid="back-btn">
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl sm:text-3xl font-extrabold text-slate-800" style={{ fontFamily: 'Nunito' }}>
              AI Insights
            </h1>
            <p className="text-sm text-slate-500">{child?.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Button
            variant="outline"
            onClick={() => navigate(`/dashboard/ai-progress/${childId}`)}
            className="rounded-full border-purple-200 text-purple-700 hover:bg-purple-50 font-bold gap-2"
            data-testid="progress-reports-btn"
          >
            <TrendingUp className="w-4 h-4" /> Progress Reports
          </Button>
          <label className="cursor-pointer" data-testid="upload-intake-btn">
            <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold transition-colors ${
              uploading ? 'bg-slate-100 text-slate-400' : 'bg-[#ECFEFF] text-[#0E7490] hover:bg-[#CFFAFE]'
            }`}>
              {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
              {uploading ? 'Processing...' : intakeProfile ? 'Re-upload Intake PDF' : 'Upload Intake PDF'}
            </div>
            <input type="file" accept=".pdf" className="hidden" onChange={handleUploadPDF} disabled={uploading} />
          </label>
          <Button
            onClick={generateInsights}
            disabled={generating}
            className="rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold gap-2"
            data-testid="generate-insights-btn"
          >
            {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
            {generating ? 'Generating...' : insights ? 'Refresh Insights' : 'Generate AI Insights'}
          </Button>
        </div>
      </div>

      {/* No data state */}
      {!intakeProfile && !insights && (
        <Card className="rounded-3xl border-dashed border-2 border-slate-200 mb-6">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Brain className="w-16 h-16 text-slate-300 mb-4" />
            <p className="text-lg font-bold text-slate-400 mb-2" style={{ fontFamily: 'Nunito' }}>No intake profile yet</p>
            <p className="text-sm text-slate-400 text-center max-w-md mb-4">
              Upload the child's intake form PDF to get started. The AI will analyze it and provide personalized session recommendations.
            </p>
            <label className="cursor-pointer" data-testid="upload-intake-empty-btn">
              <div className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#22D3EE] text-white font-bold hover:bg-[#06B6D4] transition-colors cursor-pointer">
                <Upload className="w-5 h-5" /> Upload Intake Form PDF
              </div>
              <input type="file" accept=".pdf" className="hidden" onChange={handleUploadPDF} />
            </label>
          </CardContent>
        </Card>
      )}

      {/* AI Disclaimer */}
      {(intakeProfile || insights) && (
        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4 mb-4" data-testid="ai-disclaimer">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-bold text-amber-800">AI-Generated Content - For Reference Only</p>
              <p className="text-xs text-amber-700 mt-1 leading-relaxed">
                All insights on this page are generated by artificial intelligence and are intended as a supplementary tool only.
                Do not rely solely on this information for making decisions about a child's program.
                AI interpretations may be inaccurate or incomplete. Always use your professional judgment.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      {(intakeProfile || insights) && (
        <>
          {/* Knowledge sources banner */}
          <div className="mb-4 p-3 bg-slate-50 rounded-2xl border border-slate-100" data-testid="knowledge-sources">
            <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-1">AI trained on</p>
            <div className="flex flex-wrap gap-2">
              <a href="https://learn.autism-swim.org/our-courses/neurodiversity-and-aquatics-certification/" target="_blank" rel="noreferrer" className="text-[11px] text-[#0E7490] hover:underline font-medium">Autism Swim Certification</a>
              <span className="text-slate-300">|</span>
              <a href="https://alison.com/course/introduction-to-autism-awareness-and-support" target="_blank" rel="noreferrer" className="text-[11px] text-[#0E7490] hover:underline font-medium">Autism Awareness & Support</a>
              <span className="text-slate-300">|</span>
              <a href="https://autismpartnershipfoundation.org/free-rbt-training/" target="_blank" rel="noreferrer" className="text-[11px] text-[#0E7490] hover:underline font-medium">RBT Training (ABA)</a>
            </div>
          </div>

          <div className="flex gap-1 mb-6 overflow-x-auto pb-1">
            {tabs.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold whitespace-nowrap transition-colors ${
                  activeTab === tab.id ? 'bg-[#1E1B4B] text-white' : 'text-slate-500 hover:bg-slate-100'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <tab.icon className="w-4 h-4" /> {tab.label}
              </button>
            ))}
          </div>

          {/* Overview Tab */}
          {activeTab === 'overview' && insights && (
            <div className="space-y-4" data-testid="overview-tab">
              {/* Summary */}
              <Card className="rounded-3xl bg-gradient-to-br from-[#1E1B4B] to-[#312E81] text-white">
                <CardContent className="p-6">
                  <div className="flex items-center gap-2 mb-3">
                    <Brain className="w-5 h-5 text-[#22D3EE]" />
                    <h3 className="font-bold text-lg" style={{ fontFamily: 'Nunito' }}>AI Summary</h3>
                  </div>
                  <p className="text-slate-200 text-sm leading-relaxed">{insights.overall_summary}</p>
                  {insights.generated_at && (
                    <p className="text-xs text-slate-400 mt-3">Generated {new Date(insights.generated_at).toLocaleString()}</p>
                  )}
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* What Works */}
                {insights.what_works?.length > 0 && (
                  <Card className="rounded-3xl" data-testid="what-works">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-green-500" /> What Works
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {insights.what_works.map((item, i) => (
                          <li key={`insight-${i}`} className="flex items-start gap-2 text-sm text-slate-600">
                            <div className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5 shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}

                {/* What to Avoid */}
                {insights.what_to_avoid?.length > 0 && (
                  <Card className="rounded-3xl" data-testid="what-to-avoid">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4 text-amber-500" /> What to Avoid
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="space-y-2">
                        {insights.what_to_avoid.map((item, i) => (
                          <li key={`insight-${i}`} className="flex items-start gap-2 text-sm text-slate-600">
                            <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-1.5 shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                )}
              </div>

              {/* Next Session Focus */}
              {insights.next_session_focus?.length > 0 && (
                <Card className="rounded-3xl border-[#22D3EE]/20 bg-[#ECFEFF]/30" data-testid="next-focus">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Target className="w-4 h-4 text-[#0E7490]" /> Next Session Focus
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      {insights.next_session_focus.map((item, i) => (
                        <div key={`focus-${i}`} className="flex items-start gap-3 p-3 bg-white rounded-xl">
                          <Badge className="rounded-full bg-[#22D3EE] text-white text-xs shrink-0">{i + 1}</Badge>
                          <p className="text-sm text-slate-600">{item}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {activeTab === 'overview' && !insights && (
            <Card className="rounded-3xl border-dashed border-2 border-slate-200">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Sparkles className="w-12 h-12 text-slate-300 mb-3" />
                <p className="text-slate-400 font-semibold mb-2">No insights generated yet</p>
                <p className="text-sm text-slate-400 mb-4">Click "Generate AI Insights" to analyze this child's data</p>
                <Button onClick={generateInsights} disabled={generating} className="rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold gap-2" data-testid="generate-insights-empty-btn">
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  Generate AI Insights
                </Button>
              </CardContent>
            </Card>
          )}

          {/* Intake Profile Tab */}
          {activeTab === 'profile' && (
            <div className="space-y-4" data-testid="profile-tab">
              {intakeProfile ? (
                <>
                  {/* Key Takeaways */}
                  {intakeProfile.key_takeaways?.length > 0 && (
                    <Card className="rounded-3xl bg-gradient-to-br from-amber-50 to-orange-50 border-amber-100">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-base flex items-center gap-2">
                          <Lightbulb className="w-4 h-4 text-amber-500" /> Key Takeaways
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <ul className="space-y-2">
                          {intakeProfile.key_takeaways.map((item, i) => (
                            <li key={`ai-item-${i}`} className="flex items-start gap-2 text-sm text-slate-700">
                              <div className="w-1.5 h-1.5 bg-amber-400 rounded-full mt-1.5 shrink-0" />
                              {item}
                            </li>
                          ))}
                        </ul>
                      </CardContent>
                    </Card>
                  )}

                  {/* Profile fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {[
                      { key: 'diagnosis', label: 'Diagnosis', icon: FileText },
                      { key: 'sensory_profile', label: 'Sensory Profile', icon: Waves },
                      { key: 'behavioral_triggers', label: 'Behavioral Triggers', icon: AlertTriangle },
                      { key: 'communication_style', label: 'Communication Style', icon: Brain },
                      { key: 'learning_style', label: 'Learning Style', icon: Lightbulb },
                      { key: 'affinities_interests', label: 'Affinities & Interests', icon: Sparkles },
                      { key: 'physical_motor_skills', label: 'Physical & Motor Skills', icon: Dumbbell },
                      { key: 'emotional_factors', label: 'Emotional Factors', icon: Shield },
                      { key: 'parent_input', label: 'Parent Input', icon: FileText },
                      { key: 'environmental_adaptations', label: 'Environmental Adaptations', icon: Eye },
                      { key: 'medical_safety', label: 'Medical & Safety', icon: Shield },
                    ].filter(f => intakeProfile[f.key]).map(field => (
                      <Card key={field.key} className="rounded-3xl">
                        <CardHeader className="pb-2">
                          <CardTitle className="text-sm flex items-center gap-2 text-slate-600">
                            <field.icon className="w-4 h-4 text-[#0E7490]" /> {field.label}
                          </CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-slate-700 leading-relaxed">{intakeProfile[field.key]}</p>
                        </CardContent>
                      </Card>
                    ))}
                  </div>

                  {/* Friend Instructor */}
                  {intakeProfile.friend_instructor_consent && (
                    <Card className="rounded-3xl">
                      <CardHeader className="pb-2">
                        <CardTitle className="text-sm flex items-center gap-2 text-slate-600">
                          <Waves className="w-4 h-4 text-[#0E7490]" /> Friend-Instructor Format
                        </CardTitle>
                      </CardHeader>
                      <CardContent>
                        <Badge className={`rounded-full ${
                          intakeProfile.friend_instructor_consent === 'yes' ? 'bg-green-100 text-green-700' :
                          intakeProfile.friend_instructor_consent === 'no_prefer_not' ? 'bg-red-100 text-red-700' :
                          'bg-amber-100 text-amber-700'
                        }`}>
                          {intakeProfile.friend_instructor_consent === 'yes' ? 'Willing to participate' :
                           intakeProfile.friend_instructor_consent === 'no_suggest_other' ? 'Suggests another person' :
                           intakeProfile.friend_instructor_consent === 'no_prefer_not' ? 'Prefers not to use' : 'Unknown'}
                        </Badge>
                        {intakeProfile.friend_instructor_notes && (
                          <p className="text-sm text-slate-600 mt-2">{intakeProfile.friend_instructor_notes}</p>
                        )}
                      </CardContent>
                    </Card>
                  )}
                </>
              ) : (
                <Card className="rounded-3xl border-dashed border-2 border-slate-200">
                  <CardContent className="flex flex-col items-center justify-center py-12">
                    <FileText className="w-12 h-12 text-slate-300 mb-3" />
                    <p className="text-slate-400 font-semibold mb-2">No intake profile uploaded</p>
                    <label className="cursor-pointer">
                      <div className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-[#22D3EE] text-white font-bold hover:bg-[#06B6D4] transition-colors cursor-pointer text-sm">
                        <Upload className="w-4 h-4" /> Upload Intake PDF
                      </div>
                      <input type="file" accept=".pdf" className="hidden" onChange={handleUploadPDF} />
                    </label>
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {/* Session Planning Tab */}
          {activeTab === 'planning' && insights && (
            <div className="space-y-4" data-testid="planning-tab">
              {/* Session Planning */}
              {insights.session_planning && (
                <Card className="rounded-3xl">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Target className="w-4 h-4 text-[#0E7490]" /> Session Planning
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {insights.session_planning.recommended_duration && (
                      <div className="p-3 bg-slate-50 rounded-xl">
                        <p className="text-xs font-bold text-slate-400 uppercase mb-1">Duration</p>
                        <p className="text-sm text-slate-700">{insights.session_planning.recommended_duration}</p>
                      </div>
                    )}
                    {insights.session_planning.warm_up_suggestions?.length > 0 && (
                      <div className="p-3 bg-green-50 rounded-xl">
                        <p className="text-xs font-bold text-green-500 uppercase mb-1">Warm-up</p>
                        <ul className="space-y-1">
                          {insights.session_planning.warm_up_suggestions.map((s, i) => (
                            <li key={`ai-item-${i}`} className="text-sm text-slate-700 flex items-start gap-2">
                              <div className="w-1.5 h-1.5 bg-green-400 rounded-full mt-1.5 shrink-0" />{s}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {insights.session_planning.skill_progression?.length > 0 && (
                      <div className="p-3 bg-blue-50 rounded-xl">
                        <p className="text-xs font-bold text-blue-500 uppercase mb-1">Skill Progression</p>
                        <ul className="space-y-1">
                          {insights.session_planning.skill_progression.map((s, i) => (
                            <li key={`ai-item-${i}`} className="text-sm text-slate-700 flex items-start gap-2">
                              <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mt-1.5 shrink-0" />{s}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {insights.session_planning.cool_down_suggestions?.length > 0 && (
                      <div className="p-3 bg-purple-50 rounded-xl">
                        <p className="text-xs font-bold text-purple-500 uppercase mb-1">Cool-down</p>
                        <ul className="space-y-1">
                          {insights.session_planning.cool_down_suggestions.map((s, i) => (
                            <li key={`ai-item-${i}`} className="text-sm text-slate-700 flex items-start gap-2">
                              <div className="w-1.5 h-1.5 bg-purple-400 rounded-full mt-1.5 shrink-0" />{s}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Equipment */}
              {insights.equipment?.length > 0 && (
                <Card className="rounded-3xl" data-testid="equipment-section">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Dumbbell className="w-4 h-4 text-[#0E7490]" /> Recommended Equipment
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {insights.equipment.map((eq, i) => (
                        <div key={`pattern-${i}`} className="p-3 bg-slate-50 rounded-xl">
                          <p className="text-sm font-semibold text-slate-800">{eq.item}</p>
                          <p className="text-xs text-slate-500 mt-0.5">{eq.reason}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Environment */}
              {insights.environment && (
                <Card className="rounded-3xl" data-testid="environment-section">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Waves className="w-4 h-4 text-[#0E7490]" /> Environment
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {insights.environment.ideal_conditions?.length > 0 && (
                      <div>
                        <p className="text-xs font-bold text-green-500 uppercase mb-1">Ideal Conditions</p>
                        <div className="flex flex-wrap gap-1.5">
                          {insights.environment.ideal_conditions.map((c, i) => (
                            <Badge key={`badge-${i}`} className="rounded-full bg-green-50 text-green-700 border-green-200 font-normal text-xs">{c}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {insights.environment.avoid?.length > 0 && (
                      <div>
                        <p className="text-xs font-bold text-red-500 uppercase mb-1">Avoid</p>
                        <div className="flex flex-wrap gap-1.5">
                          {insights.environment.avoid.map((c, i) => (
                            <Badge key={`badge-${i}`} className="rounded-full bg-red-50 text-red-700 border-red-200 font-normal text-xs">{c}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                    {insights.environment.sensory_accommodations?.length > 0 && (
                      <div>
                        <p className="text-xs font-bold text-amber-500 uppercase mb-1">Sensory Accommodations</p>
                        <div className="flex flex-wrap gap-1.5">
                          {insights.environment.sensory_accommodations.map((c, i) => (
                            <Badge key={`badge-${i}`} className="rounded-full bg-amber-50 text-amber-700 border-amber-200 font-normal text-xs">{c}</Badge>
                          ))}
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Teaching Approach */}
              {insights.teaching_approach && (
                <Card className="rounded-3xl" data-testid="teaching-approach-section">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-base flex items-center gap-2">
                      <Lightbulb className="w-4 h-4 text-[#0E7490]" /> Teaching Approach
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {insights.teaching_approach.communication_methods?.length > 0 && (
                      <div className="p-3 bg-blue-50 rounded-xl">
                        <p className="text-xs font-bold text-blue-500 uppercase mb-1">Communication</p>
                        <ul className="space-y-1">
                          {insights.teaching_approach.communication_methods.map((m, i) => (
                            <li key={`ai-item-${i}`} className="text-sm text-slate-700">{m}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {insights.teaching_approach.motivation_strategies?.length > 0 && (
                      <div className="p-3 bg-green-50 rounded-xl">
                        <p className="text-xs font-bold text-green-500 uppercase mb-1">Motivation</p>
                        <ul className="space-y-1">
                          {insights.teaching_approach.motivation_strategies.map((m, i) => (
                            <li key={`ai-item-${i}`} className="text-sm text-slate-700">{m}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                    {insights.teaching_approach.pacing_notes && (
                      <div className="p-3 bg-amber-50 rounded-xl">
                        <p className="text-xs font-bold text-amber-500 uppercase mb-1">Pacing</p>
                        <p className="text-sm text-slate-700">{insights.teaching_approach.pacing_notes}</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>
          )}

          {activeTab === 'planning' && !insights && (
            <Card className="rounded-3xl border-dashed border-2 border-slate-200">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Target className="w-12 h-12 text-slate-300 mb-3" />
                <p className="text-slate-400 font-semibold">Generate AI insights to see session planning suggestions</p>
              </CardContent>
            </Card>
          )}

          {/* Patterns Tab */}
          {activeTab === 'patterns' && insights && insights.patterns_observed?.length > 0 && (
            <div className="space-y-4" data-testid="patterns-tab">
              <Card className="rounded-3xl">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Eye className="w-4 h-4 text-[#0E7490]" /> Observed Patterns
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {insights.patterns_observed.map((p, i) => (
                      <div key={`pattern-${i}`} className="p-4 bg-slate-50 rounded-xl">
                        <div className="flex items-start justify-between gap-3 mb-1">
                          <p className="text-sm font-semibold text-slate-800">{p.pattern}</p>
                          <Badge className={`rounded-full shrink-0 text-xs ${
                            p.confidence === 'high' ? 'bg-green-100 text-green-700' :
                            p.confidence === 'medium' ? 'bg-amber-100 text-amber-700' :
                            'bg-slate-100 text-slate-500'
                          }`}>
                            {p.confidence}
                          </Badge>
                        </div>
                        <p className="text-sm text-slate-500">{p.recommendation}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            </div>
          )}

          {activeTab === 'patterns' && (!insights || !insights.patterns_observed?.length) && (
            <Card className="rounded-3xl border-dashed border-2 border-slate-200">
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Eye className="w-12 h-12 text-slate-300 mb-3" />
                <p className="text-slate-400 font-semibold mb-1">No patterns detected yet</p>
                <p className="text-sm text-slate-400">Patterns emerge as more sessions are completed</p>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
}
